import CustomIcon from '@/components/icons/CustomIcon';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { getFindGravePath } from '../../../../../service/constants';
import { cachedJsonFetch } from '../../../../../service/cache';
import { fetch_request } from '../../../../../service/request';
import Content from '../Component/Content';
import Header from '../Component/Header';
import Paginator from '../Component/Paginator';
import styles from '../Municipality/Index.module.css';

const Index = (props) => {
  const intl = useIntl();
  const [searchInputValue, setSearchInputValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const municipalitySlug = props.municipalitySlug;
  const [cemeteriesCount, setCemeteriesCount] = useState(0);
  const [cemeteriesList, setCemeteriesList] = useState([]);
  const [municipalityName, setMunicipalityName] = useState(props.municipalityName || '');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchInputValue]);

  useEffect(() => {
    if (!municipalitySlug) return;
    let cancelled = false;

    const loadCount = async () => {
      try {
        const { data } = await cachedJsonFetch(
          `/api/cemeteries_count?municipality_slug=${municipalitySlug}`,
          {
            fetcher: fetch_request,
            cacheKey: `cemeteries-count:${municipalitySlug}`,
            ttlMs: 10 * 60 * 1000,
          },
        );
        if (cancelled) return;
        setCemeteriesCount(data?.count || 0);
      } catch (err) {
        console.log(err);
      }
    };

    loadCount();
    return () => {
      cancelled = true;
    };
  }, [municipalitySlug]);

  useEffect(() => {
    if (props.municipalityName) {
      setMunicipalityName(props.municipalityName);
    }
  }, [props.municipalityName]);

  useEffect(() => {
    if (!municipalitySlug) return;
    let cancelled = false;

    const fetchCemeteries = async () => {
      setLoading(true);
      const url = `/api/search_cemeteries?input=${searchInputValue}&page=${currentPage}&municipality_slug=${municipalitySlug}`;
      try {
        const { data } = await cachedJsonFetch(url, {
          fetcher: fetch_request,
          cacheKey: `search-cemeteries:${municipalitySlug}:${searchInputValue || 'all'}:${currentPage}`,
          ttlMs: 5 * 60 * 1000,
        });
        if (cancelled) return;
        setCemeteriesList(data?.cemeteries || []);
        setTotalPages(data?.total_pages || 0);
      } catch (err) {
        console.log(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCemeteries();
    return () => {
      cancelled = true;
    };
  }, [currentPage, municipalitySlug, searchInputValue]);

  const Description = () => {
    return (
      <span>
        <FormattedMessage id="it_is" />{' '}
        <span>
          <FormattedMessage id="find_grave_count" values={{ count: cemeteriesCount }} />
        </span>{' '}
        <FormattedMessage id="cemetery_description" values={{ municipalityName }} />
      </span>
    );
  };

  const headerTitle = municipalityName ? (
    <FormattedMessage id="find_cemetery" values={{ municipalityName }} />
  ) : (
    <FormattedMessage id="find_cemetery_generic" />
  );

  return (
    <div>
      <Header
        title={headerTitle}
        description={<Description />}
        setSearchInputValue={setSearchInputValue}
        placeholder={intl.formatMessage({ id: 'search_cemetery' })}
      />

      <Content
        searchInputValue={searchInputValue}
        list={cemeteriesList}
        url={`${getFindGravePath()}/${municipalitySlug}`}
        logo={<CustomIcon name="tombstone" lib="lucide" size={150} style={{ color: 'var(--sky-4)' }} />}
      />
      {loading && <div className={styles.loadingHint}><FormattedMessage id="loading" defaultMessage="Loading..." /></div>}
      {cemeteriesCount > 8 && (
        <Paginator
          setCurrentPage={setCurrentPage}
          currentPage={currentPage}
          totalPages={totalPages}
        />
      )}
    </div>
  );
};

export default Index;
